﻿import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  ExternalLink,
  Loader2,
  Pause,
  Play,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Trans } from "react-i18next";
import { toast } from "sonner";
import type { ShopStatus } from "@ticketnjoy/types";

import { DashboardShell } from "@/components/dashboard-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { QueryError } from "@/components/query-error";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuthenticatedMe } from "@/features/organizers/api";
import {
  useShopQuery,
  useShopStatusMutation,
  useUpdateShopMutation,
} from "@/features/shops/api";
import { ShopForm, type EditShopFormValues } from "@/features/shops/shop-form";
import { EmbedCodeCard } from "@/features/shops/embed-code-card";
import { ShopPreviewCard } from "@/features/shops/shop-preview-card";
import { TrackingCard } from "@/features/shops/tracking-card";
import { ShopEventsSection } from "@/features/shop-events/shop-events-section";
import { PaymentMethodsSection } from "@/features/payment-methods/payment-methods-section";
import { ApiError, isNotFoundError } from "@/lib/api-client";
import { useConfirm } from "@/lib/confirm";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

const STATUS_VARIANTS: Record<ShopStatus, "success" | "warning" | "secondary"> = {
  Active: "success",
  Paused: "warning",
  Archived: "secondary",
};

export function ShopDetailPage() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug ?? "";
  const navigate = useNavigate();

  const me = useAuthenticatedMe();
  const shopQuery = useShopQuery(slug);
  const update = useUpdateShopMutation(slug);
  const statusMutation = useShopStatusMutation(slug);
  const confirm = useConfirm();
  const tConfirm = useT("confirm.archiveShop");
  const t = useT("shops.detail");
  const tShops = useT("shops");
  const fmt = useFormat();

  if (!me.data) return null;

  if (shopQuery.isLoading) {
    return (
      <DashboardShell companyName={me.data.companyName}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardShell>
    );
  }

  if (shopQuery.error && !isNotFoundError(shopQuery.error)) {
    return (
      <DashboardShell companyName={me.data.companyName}>
        <QueryError
          message={t("loadError")}
          onRetry={() => shopQuery.refetch()}
          retrying={shopQuery.isFetching}
        />
      </DashboardShell>
    );
  }

  if (shopQuery.error || !shopQuery.data) {
    return (
      <DashboardShell companyName={me.data.companyName}>
        <Card className="max-w-md mx-auto text-center">
          <CardHeader>
            <CardTitle>{t("notFoundTitle")}</CardTitle>
            <CardDescription>{t("notFoundDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate("/shops")}>
              {t("backToShops")}
            </Button>
          </CardContent>
        </Card>
      </DashboardShell>
    );
  }

  const shop = shopQuery.data;
  const isArchived = shop.status === "Archived";
  const shopBaseUrl =
    import.meta.env.VITE_SHOP_BASE_URL ?? "http://localhost:3002";
  const publicShopUrl = `${shopBaseUrl}/${shop.slug}`;
  // Mağazayı yayına alabilmek için organizer'ın yasal bilgileri tam olmalı
  // (backend ChangeStatusAsync guard'ı bunu enforce ediyor — UI'da da engelle
  // ki kullanıcı submit etmeden sebebini görsün).
  const canActivate = me.data.isLegalInfoComplete;

  async function handleUpdate(values: EditShopFormValues) {
    try {
      await update.mutateAsync({
        name: values.name,
        description: emptyToNull(values.description),
        logoUrl: emptyToNull(values.logoUrl),
        coverImageUrl: emptyToNull(values.coverImageUrl),
        // themeColor UI'dan kaldırıldı; mevcut değeri round-trip ediyoruz
        // (aksi halde backend null'layacak). v2 tema ayarları bölümünde geri gelecek.
        themeColor: shop.themeColor,
        contactEmail: emptyToNull(values.contactEmail),
        contactPhone: emptyToNull(values.contactPhone),
        defaultLocale: values.defaultLocale,
        heroStyle: values.heroStyle,
        storefrontType: values.storefrontType,
        allowedEmbedDomains: values.allowedEmbedDomains ?? [],
      });
      toast.success(t("updated"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("updateFailed"));
    }
  }

  async function handleStatusChange(action: "pause" | "activate" | "archive") {
    if (action === "archive") {
      const ok = await confirm({
        title: tConfirm("title"),
        description: tConfirm("description"),
        confirmLabel: tConfirm("action"),
        destructive: true,
      });
      if (!ok) return;
    }

    try {
      await statusMutation.mutateAsync(action);
      toast.success(
        action === "pause"
          ? t("paused")
          : action === "activate"
            ? t("activated")
            : t("archived"),
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("actionFailed"));
    }
  }

  return (
    <DashboardShell companyName={me.data.companyName}>
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild className="mb-3 -ms-2">
          <Link to="/shops">
            <ArrowLeft className="me-1 h-4 w-4 rtl:-scale-x-100" />
            {tShops("title")}
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{shop.name}</h1>
              <Badge variant={STATUS_VARIANTS[shop.status]}>
                {tShops(`status.${shop.status}`)}
              </Badge>
            </div>
            <a
              href={publicShopUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block font-mono text-sm text-muted-foreground transition hover:text-foreground"
              title={t("openInNewTab")}
            >
              {publicShopUrl}
            </a>
          </div>
          <Button
            variant="outline"
            size="sm"
            asChild
            disabled={isArchived}
            title={isArchived ? t("archivedNoView") : t("newTab")}
          >
            <a
              href={publicShopUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={isArchived}
              onClick={(e) => {
                if (isArchived) e.preventDefault();
              }}
            >
              <ExternalLink className="me-1.5 h-4 w-4" />
              {t("view")}
            </a>
          </Button>
        </div>
      </div>

      {!isArchived && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <ShopEventsSection shopSlug={slug} />
          </CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">{t("paymentMethodsTitle")}</CardTitle>
          <CardDescription>{t("paymentMethodsDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <PaymentMethodsSection shopSlug={slug} disabled={isArchived} />
        </CardContent>
      </Card>

      {!isArchived ? (
        <div className="mb-6">
          <ShopPreviewCard
            shopSlug={slug}
            storefrontType={shop.storefrontType}
            updatedAt={shop.updatedAt}
          />
        </div>
      ) : null}

      {/* Embed kodu kartı sadece Embed tipindeki shop'larda görünür. Link/Site
          tiplerinde organizer kendi sitesine widget gömmeye niyetli değil →
          UI'ı kalabalık etmemek için gizliyoruz. */}
      {shop.storefrontType === "Embed" && !isArchived ? (
        <div className="mb-6">
          <EmbedCodeCard shopSlug={slug} />
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("generalCardTitle")}</CardTitle>
            <CardDescription>{t("generalCardDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <fieldset disabled={isArchived} className="space-y-4">
              <ShopForm
                mode="edit"
                shopId={shop.id}
                defaultValues={{
                  name: shop.name,
                  description: shop.description ?? "",
                  defaultLocale: shop.defaultLocale,
                  logoUrl: shop.logoUrl ?? "",
                  coverImageUrl: shop.coverImageUrl ?? "",
                  contactEmail: shop.contactEmail ?? "",
                  contactPhone: shop.contactPhone ?? "",
                  heroStyle: shop.heroStyle,
                  storefrontType: shop.storefrontType,
                  allowedEmbedDomains: shop.allowedEmbedDomains ?? [],
                }}
                onSubmit={handleUpdate}
                isSubmitting={update.isPending}
              />
              {isArchived && (
                <p className="text-sm text-muted-foreground">{t("archivedCannotEdit")}</p>
              )}
            </fieldset>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("statusCardTitle")}</CardTitle>
              <CardDescription>{t("statusCardDescription")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {shop.status === "Active" && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleStatusChange("pause")}
                  disabled={statusMutation.isPending}
                >
                  <Pause className="me-2 h-4 w-4" />
                  {t("pause")}
                </Button>
              )}
              {shop.status === "Paused" && (
                <>
                  {!canActivate && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/50 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="space-y-1">
                        <p>
                          <Trans
                            i18nKey="shops.detail.legalRequired"
                            components={{
                              link: (
                                <Link
                                  to="/settings"
                                  className="font-medium underline hover:no-underline"
                                />
                              ),
                            }}
                          />
                        </p>
                      </div>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleStatusChange("activate")}
                    disabled={statusMutation.isPending || !canActivate}
                    title={!canActivate ? t("legalMissing") : t("activateTitle")}
                  >
                    <Play className="me-2 h-4 w-4" />
                    {t("activate")}
                  </Button>
                </>
              )}
              {!isArchived && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-destructive hover:text-destructive"
                  onClick={() => handleStatusChange("archive")}
                  disabled={statusMutation.isPending}
                >
                  <Archive className="me-2 h-4 w-4" />
                  {t("archive")}
                </Button>
              )}
              {isArchived && (
                <p className="text-sm text-muted-foreground">{t("archivedNotice")}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("infoCardTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm text-muted-foreground">
              <p>
                <span className="text-foreground">{t("defaultLocale")}:</span>{" "}
                <span className="uppercase">{shop.defaultLocale}</span>
              </p>
              <p>
                <span className="text-foreground">{t("createdAt")}:</span>{" "}
                {fmt.date(shop.createdAt)}
              </p>
              <p>
                <span className="text-foreground">{t("updatedAt")}:</span>{" "}
                {fmt.date(shop.updatedAt)}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mt-6">
        <TrackingCard shopSlug={slug} tracking={shop.tracking} disabled={isArchived} />
      </div>
    </DashboardShell>
  );
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
